/**
 * The words the arrange board's reading strip says about a run: a key signature, a player's
 * part, and how much the verdict graded. Pure: each takes what the run returned and returns
 * the text, so {@link ../components/ArrangeReading} only lays them out.
 */

import { handedIn, type KeptStaff, type Verdict } from "@app/shared-types";
import { count, rate } from "./format";

/**
 * A key signature as the number of sharps or flats it prints.
 *
 * Not as a key name: a signature of two sharps is D major and B minor equally, the source is
 * not obliged to say which, and naming one of them here would be this client inventing a fact
 * the arranger never read. What the strip can say without guessing is what is on the page.
 */
export function signature(fifths: number): string {
  if (fifths === 0) return "none";
  const printed = Math.abs(fifths);
  return `${printed} ${fifths > 0 ? "sharp" : "flat"}${printed === 1 ? "" : "s"}`;
}

export function plural(amount: number, one: string, many = `${one}s`): string {
  return `${count(amount)} ${amount === 1 ? one : many}`;
}

/** A line a player doubles, as the words before the player it doubles. */
const LINES: Readonly<Record<NonNullable<KeptStaff["doubles"]>["line"], string>> = {
  tune: "the tune",
  bass: "the bass",
  inner: "the inner line",
};

/**
 * What one player was given, as the words after "plays". `beside` is whether a player of two
 * hands plays beside other players: there the players take the tune, and the hands only what no
 * player plays. `model` is whether the note model wrote the parts under the tune: two hands
 * are then written every one of them, each note in the hand that can take it. `stops` is
 * whether the tune's player is a bowed string or a guitar alone, which stops what it can of
 * them. `feet` is whether the staff is an organist's pedal staff or the left hand over it,
 * where the hands are dealt their notes: the feet then take the bass before the hand does.
 */
function part(
  staff: KeptStaff,
  beside: boolean,
  model: boolean,
  stops: boolean,
  feet: boolean,
): string {
  switch (staff.plays) {
    case "tune":
      if (staff.strummed > 0) {
        return "the tune, and under it the bass and the notes between that its hand can stop";
      }
      return stops
        ? "the tune, and under it what it can stop of the parts the note model wrote"
        : "the tune";
    case "chords":
      return "the bass where no player plays it and the notes between that its hand can stop";
    case "bass":
      if (!trod(staff)) return "the bass";
      if (!feet) return "the pedal staff of the part its hands play as written";
      return model
        ? "on the pedal-board, of the parts the note model wrote under the tune the lowest note struck at a moment, where it lies under middle C and under every note the hands hold"
        : "the bass on the pedal-board, each note of it that lies there within the feet's reach";
    case "part":
      return `the ${staff.from ?? "source"} part as written`;
    case "doubling":
      if (staff.doubles === null) return "a line a second time";
      return staff.turned > 0
        ? `${LINES[staff.doubles.line]} with ${staff.doubles.player}, by turns`
        : `${LINES[staff.doubles.line]} ${staff.doubles.player} plays, a second time`;
    case "harmony":
      return model
        ? "a part the note model wrote under the tune, which the original does not have"
        : "notes of the chord the original does not have";
    case "counter":
      return "a counter-melody against the tune, which the original does not have";
    case "right":
      if (model) {
        return beside
          ? "what its hand takes of the parts the note model wrote under the players' tune"
          : "the tune on top and what its hand takes of the parts the note model wrote under it";
      }
      return beside
        ? "the notes between that no player plays, under the players' tune"
        : "the tune on top and the notes between that its hand can reach";
    case "left":
      if (model) return "what its hand takes of the parts the note model wrote under the tune";
      if (feet) {
        return beside
          ? "the bass where neither a player nor the feet play it and the notes between that no player plays"
          : "the bass where the feet cannot play it and the notes between that its hand can reach";
      }
      return beside
        ? "the bass where no player plays it and the notes between that no player plays"
        : "the bass underneath and the notes between that its hand can reach";
    default:
      return "notes between them";
  }
}

/** Whether `staff` is an organist's pedal staff: the bass, on an instrument of two hands. */
export function trod(staff: KeptStaff): boolean {
  return staff.plays === "bass" && handedIn([staff.instrument]).length > 0;
}

/** What one player was given, as a sentence; `beside`, `model` and `stops` as for {@link part}. */
export function given(
  staff: KeptStaff,
  beside: boolean,
  model: boolean,
  stops: boolean,
  feet: boolean,
): string {
  const octaves = Math.abs(staff.shift) / 12;
  const moved =
    staff.shift === 0
      ? ""
      : `, ${staff.strummed > 0 ? "the tune " : ""}${plural(octaves, "octave")} ${staff.shift > 0 ? "up" : "down"} from where it was written`;
  if (staff.notes === 0) return `${staff.name} is given nothing and rests throughout.`;
  const added =
    staff.added === 0 || staff.plays === "harmony" || staff.plays === "counter"
      ? ""
      : `, ${count(staff.added)} of them added`;
  // A player with a line of its own that takes turns on another's is told both.
  const shared =
    staff.plays === "doubling" || staff.doubles === null
      ? ""
      : staff.turned === 0
        ? `, and ${LINES[staff.doubles.line]} ${staff.doubles.player} plays, where it is loud`
        : `, and ${LINES[staff.doubles.line]} with ${staff.doubles.player}, by turns`;
  return `${staff.name} plays ${part(staff, beside, model, stops, feet)}${moved}${shared}: ${plural(staff.notes, "note")}${added}, resting ${rate(staff.resting)} of the time.`;
}

/**
 * The verdict as the strip prints it, and each check's count behind it. A rate is only as good as what
 * it rated: where the writing checks -- spacing, doubling and the rest -- found nothing they
 * grade, the one check that spoke is the compass rule, and a rate of it would read as a clean
 * arrangement, so the strip says "range only" instead of a percentage. Otherwise the rate is
 * printed beside how many things the writing checks graded -- chords, pairs of chords, moments
 * and phrases, each check its own -- so four graded reads as four, not as a pass mark.
 */
export function graded(verdict: Verdict): { value: string; title: string; judged: number } {
  const writing = verdict.judgements.filter((one) => one.predicate !== "compass");
  const judged = writing.reduce((sum, one) => sum + one.judged, 0);
  const spoke = verdict.judgements
    .filter((one) => one.judged > 0)
    .map((one) => `${one.predicate} ${count(one.clean)} of ${count(one.judged)} clean`);
  const title = spoke.length === 0 ? "No check found anything to grade." : spoke.join(", ");
  if (judged === 0) return { value: "range only", title, judged };
  return { value: `${rate(verdict.score)} of ${count(judged)} graded`, title, judged };
}

/** `said`, which begins in lower case, begun as a sentence. */
export function sentence(said: string): string {
  return said.charAt(0).toUpperCase() + said.slice(1);
}
